import React, { useEffect, useState } from 'react';
import {
  Box, Checkbox, Chip, Table, TableHead, TableRow, TableCell, TableBody, Typography,
} from '@mui/material';
import PharmacyMedicineAutocomplete from './PharmacyMedicineAutocomplete';
import ControlledDrugBadge from './ControlledDrugBadge';
import { InvTextField } from './InvField';
import { IPD_COLORS } from '../theme';
import { formatCurrency, formatDate, daysUntilExpiry, lineTotal, computeLineDiscountAmount, parseMoney } from '../utils';
import useInvRole from '../hooks/useInvRole';
import { INV_ACTIONS } from '../utils/inventoryRoles';

const NEAR_EXPIRY_DAYS = 90;

function batchAvailableQty(batch) {
  return Number(batch?.available_quantity ?? batch?.quantity ?? batch?.stock ?? 0) || 0;
}

function batchMaterialGroup(batch, item) {
  return batch?.material_group
    ?? item?._raw?.material_group?.group_name
    ?? item?._raw?.material_group?.name
    ?? item?._raw?.material_group
    ?? '';
}

function batchWac(item) {
  const wac = item?._raw?.wac ?? item?.wac;
  return wac != null && wac !== '' ? parseMoney(wac) : null;
}

function isExpired(expiryDate) {
  const days = daysUntilExpiry(expiryDate);
  return days != null && days < 0;
}

function isNearExpiry(expiryDate) {
  const days = daysUntilExpiry(expiryDate);
  return days != null && days >= 0 && days < NEAR_EXPIRY_DAYS;
}

/** Earliest-expiry, in-stock, non-expired batch — FEFO default. */
function pickFefoBatch(batches) {
  return (batches || [])
    .filter((b) => batchAvailableQty(b) > 0 && !isExpired(b.expiry_date))
    .sort((a, b) => new Date(a.expiry_date) - new Date(b.expiry_date))[0] || null;
}

function lineMrp(bl) {
  const mrp = parseMoney(bl?.mrp);
  if (mrp > 0) return mrp;
  return parseMoney(bl?.rate);
}

/** MRP markdown (+ any extra line disc) as a percent, for the Discount % cell. */
function billedDiscountPct(bl) {
  const mrp = lineMrp(bl);
  const rate = parseMoney(bl?.rate);
  const extra = Number(bl?.discount_percent) || 0;
  if (mrp <= 0) return extra;
  const payable = rate * (1 - extra / 100);
  return Math.round(((mrp - payable) / mrp) * 10000) / 100;
}

function batchLineFromBatch(batch, item, qty = 1) {
  return {
    batch_number: batch.batch_number,
    expiry_date: batch.expiry_date,
    qty,
    rate: parseMoney(batch.sale_price) || parseMoney(batch.mrp) || 0,
    mrp: parseMoney(batch.mrp) || 0,
    material_group: batchMaterialGroup(batch, item),
    wac: batchWac(item),
    available_qty: batchAvailableQty(batch),
    discount_percent: 0,
    inventory_transaction_id: batch.inventory_transaction_id || batch.transaction_id || null,
    inventory_transaction_item_id: batch.inventory_transaction_item_id || batch.transaction_item_id || batch.stock_balance_id || null,
  };
}

/** Sum of Total Basic / Total Discount / Amount Payable across medicine lines. */
export function computeLinesTotal(lines) {
  return (lines || []).reduce((acc, line) => {
    (line.batchLines || []).forEach((bl) => {
      const discPct = Number(bl.discount_percent) || 0;
      const basic = Number(bl.qty || 0) * Number(bl.rate || 0);
      const discount = computeLineDiscountAmount(bl.qty, bl.rate, discPct);
      acc.totalBasic += basic;
      acc.totalDiscount += discount;
      acc.amountPayable += lineTotal(bl.qty, bl.rate, 0, discPct);
    });
    return acc;
  }, { totalBasic: 0, totalDiscount: 0, amountPayable: 0 });
}

/**
 * Batch-wise picker for one medicine grid row: search + FEFO-defaulted batch table
 * supporting splitting a requested quantity across multiple batches.
 */
export default function PharmacyBatchPicker({
  medicineLine,
  storeId,
  store = null,
  onMedicineChange,
  onBatchLinesChange,
  isSupervisor: isSupervisorProp,
  disabled,
  requireStore = true,
}) {
  const { can } = useInvRole();
  const isSupervisor = isSupervisorProp ?? can(INV_ACTIONS.CONTROLLED_OVERRIDE);
  const item = medicineLine.medicine;
  const batchLines = medicineLine.batchLines || [];
  const [discountDraft, setDiscountDraft] = useState(null);

  useEffect(() => {
    if (!item || batchLines.length) return;
    const fefo = pickFefoBatch(item.batches);
    if (fefo) onBatchLinesChange([batchLineFromBatch(fefo, item, 1)]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, item?.batches?.length]);

  function isBatchSelected(batchNumber) {
    return batchLines.some((bl) => bl.batch_number === batchNumber);
  }

  function toggleBatch(batch, checked) {
    if (checked) {
      onBatchLinesChange([...batchLines, batchLineFromBatch(batch, item, 1)]);
    } else {
      onBatchLinesChange(batchLines.filter((bl) => bl.batch_number !== batch.batch_number));
    }
  }

  function updateBatchQty(batchNumber, qty) {
    onBatchLinesChange(batchLines.map((bl) => (bl.batch_number === batchNumber ? { ...bl, qty } : bl)));
  }

  function updateBatchDiscount(batchNumber, pctRaw) {
    const pct = Math.min(100, Math.max(0, Number(pctRaw) || 0));
    onBatchLinesChange(batchLines.map((bl) => {
      if (bl.batch_number !== batchNumber) return bl;
      const mrp = lineMrp(bl);
      const rate = Math.round(mrp * (1 - pct / 100) * 100) / 100;
      return { ...bl, rate, discount_percent: 0 };
    }));
  }

  const batches = item?.batches || [];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: batches.length ? 1 : 0 }}>
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <PharmacyMedicineAutocomplete
            value={medicineLine.medicine_id}
            valueItem={item}
            storeId={storeId}
            store={store}
            disabled={disabled}
            requireStore={requireStore}
            onChange={(itemId, picked) => {
              onMedicineChange(itemId, picked);
              onBatchLinesChange([]);
            }}
          />
        </Box>
        <ControlledDrugBadge show={!!item?.controlled} />
        {(item?._raw?.material_group?.group_name || item?._raw?.material_group) && (
          <Chip
            label={item._raw.material_group?.group_name || item._raw.material_group}
            size="small"
            sx={{ height: 20, fontSize: 10 }}
          />
        )}
      </Box>

      {item?.controlled && (
        <Typography sx={{ fontSize: 11, color: IPD_COLORS.warning.text, mb: 1 }}>
          Prescription required
        </Typography>
      )}

      {!!batches.length && (
        <Box sx={{ border: `1px solid ${IPD_COLORS.border}`, borderRadius: '8px', overflow: 'hidden', mb: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: IPD_COLORS.panelHeaderBg }}>
                <TableCell sx={{ width: 40 }} />
                <TableCell>Batch No.</TableCell>
                <TableCell>Expiry</TableCell>
                <TableCell align="right">Available Qty</TableCell>
                <TableCell align="right">MRP</TableCell>
                <TableCell align="right">Sale Price{isSupervisor ? ' / WAC' : ''}</TableCell>
                <TableCell sx={{ width: 100 }}>Qty</TableCell>
                <TableCell sx={{ width: 110 }}>Discount %</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {batches.map((batch) => {
                const expired = isExpired(batch.expiry_date);
                const nearExpiry = isNearExpiry(batch.expiry_date);
                const selected = isBatchSelected(batch.batch_number);
                const batchLine = batchLines.find((bl) => bl.batch_number === batch.batch_number);
                const avail = batchAvailableQty(batch);
                const mrp = parseMoney(batch.mrp) || 0;
                const salePrice = parseMoney(batch.sale_price) || mrp;
                const wac = batchWac(item);
                const qtyError = batchLine
                  && batchLine.qty !== ''
                  && batchLine.qty != null
                  && (Number(batchLine.qty) <= 0 || Number(batchLine.qty) > avail);

                return (
                  <TableRow key={batch.batch_number || batch.id} sx={{ opacity: expired ? 0.5 : 1 }}>
                    <TableCell>
                      <Checkbox
                        size="small"
                        checked={selected}
                        disabled={disabled || expired || avail <= 0}
                        onChange={(e) => toggleBatch(batch, e.target.checked)}
                      />
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{batch.batch_number}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                        {!expired && nearExpiry && (
                          <Box sx={{
                            width: 7, height: 7, borderRadius: '50%',
                            backgroundColor: IPD_COLORS.warning.text, flexShrink: 0,
                          }} />
                        )}
                        <Typography sx={{ fontSize: 12.5 }}>{formatDate(batch.expiry_date)}</Typography>
                        {expired && (
                          <Chip label="Expired" size="small" sx={{
                            height: 18, fontSize: 10, fontWeight: 700,
                            backgroundColor: IPD_COLORS.error.bg, color: IPD_COLORS.error.text,
                          }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: 12.5, fontWeight: 600 }}>{avail || '—'}</TableCell>
                    <TableCell align="right" sx={{ fontSize: 12.5 }}>{formatCurrency(mrp)}</TableCell>
                    <TableCell align="right">
                      <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>
                        {formatCurrency(salePrice)}
                      </Typography>
                      {salePrice < mrp && (
                        <Typography sx={{
                          fontSize: 10.5, color: IPD_COLORS.textSecondary, textDecoration: 'line-through',
                        }}>
                          MRP {formatCurrency(mrp)}
                        </Typography>
                      )}
                      {isSupervisor && (
                        <Typography sx={{ fontSize: 10.5, color: IPD_COLORS.textSecondary }}>
                          WAC {wac != null ? formatCurrency(wac) : '—'}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {selected && (
                        <InvTextField
                          type="number"
                          value={batchLine?.qty ?? ''}
                          error={qtyError ? true : undefined}
                          helperText={qtyError ? `Max ${avail}` : ''}
                          inputProps={{ min: 1, max: avail }}
                          disabled={disabled}
                          onChange={(e) => {
                            const raw = e.target.value;
                            updateBatchQty(
                              batch.batch_number,
                              raw === '' ? '' : Number(raw),
                            );
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {selected && (
                        <InvTextField
                          type="number"
                          value={
                            discountDraft?.batchNumber === batch.batch_number
                              ? discountDraft.value
                              : String(billedDiscountPct(batchLine))
                          }
                          inputProps={{ min: 0, max: 100, step: '0.01' }}
                          disabled={disabled}
                          onFocus={() => setDiscountDraft({
                            batchNumber: batch.batch_number,
                            value: String(billedDiscountPct(batchLine)),
                          })}
                          onChange={(e) => {
                            const raw = e.target.value;
                            setDiscountDraft({ batchNumber: batch.batch_number, value: raw });
                            if (raw === '' || raw === '.') return;
                            const n = Number(raw);
                            if (!Number.isFinite(n)) return;
                            updateBatchDiscount(batch.batch_number, n);
                          }}
                          onBlur={() => setDiscountDraft(null)}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
}
